import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { MdOutlineSearch, MdClose } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { CLIENTS } from "../../config/apiConfig";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { Badge } from "./Primitives";
import { initialsOf, PLAN_TONE } from "./crmHelpers";

/*
  Busca de cliente com sugestão.

  Um `<select>` com a base inteira não serve: são milhares de salões, e
  rolar uma lista dessas para achar "Studio Bella" é pior do que digitar. A
  busca usa a MESMA consulta da tabela de clientes, então encontra por nome,
  e-mail, telefone, documento e "#id" — que é como a pessoa costuma ter o
  cliente anotado. O endpoint é outro porque o seletor também serve a quem
  não tem a tabela (o operador): devolve só o que identifica a conta.

  A sugestão só dispara a partir de dois caracteres: com um só, a consulta
  volta com a base toda e a lista não ajuda ninguém.
*/

const MINIMO_PARA_BUSCAR = 2;

const ClientPicker = ({ value, onChange, disabled = false }) => {
  const [termo, setTermo] = useState("");
  const [aberto, setAberto] = useState(false);
  const debounced = useDebouncedValue(termo, 350);
  const containerRef = useRef(null);

  const { data, isFetching } = useQuery(
    ["clients", "picker", debounced],
    async () => {
      const params = new URLSearchParams({ search: debounced });
      const { data: resposta } = await axios.get(
        `${CLIENTS.PICKER}?${params.toString()}`
      );
      return resposta.data?.data || [];
    },
    { enabled: debounced.trim().length >= MINIMO_PARA_BUSCAR }
  );

  // Clicar fora fecha a lista — senão ela fica sobre o resto do formulário.
  useEffect(() => {
    const aoClicarFora = (evento) => {
      if (!containerRef.current?.contains(evento.target)) setAberto(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, []);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-pink-200 bg-pink-50/60 px-3 py-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-[10px] font-bold text-pink-700">
          {initialsOf(value.name)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-900">
            {value.name}
          </span>
          <span className="block truncate text-[11px] text-slate-500">
            #{value.id}
            {value.email ? ` · ${value.email}` : ""}
          </span>
        </span>
        <Badge tone={PLAN_TONE[value.planId] || "neutral"}>{value.planId}</Badge>
        {!disabled ? (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setTermo("");
            }}
            title="Trocar cliente"
            className="shrink-0 rounded-lg p-1 text-slate-400 transition hover:bg-white hover:text-slate-700"
          >
            <MdClose size={16} />
          </button>
        ) : null}
      </div>
    );
  }

  const resultados = data || [];
  const buscando = debounced.trim().length >= MINIMO_PARA_BUSCAR;

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 focus-within:border-pink-300 focus-within:ring-2 focus-within:ring-pink-100">
        <MdOutlineSearch size={18} className="shrink-0 text-slate-400" />
        <input
          value={termo}
          disabled={disabled}
          onChange={(evento) => {
            setTermo(evento.target.value);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          placeholder="Buscar por nome, e-mail, telefone ou #id"
          className="w-full bg-transparent py-2 text-sm outline-none"
        />
      </div>

      {aberto && termo.trim() ? (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {!buscando ? (
            <p className="px-3 py-3 text-xs text-slate-400">
              Digite ao menos {MINIMO_PARA_BUSCAR} caracteres.
            </p>
          ) : isFetching ? (
            <p className="px-3 py-3 text-xs text-slate-400">Buscando…</p>
          ) : !resultados.length ? (
            <p className="px-3 py-3 text-xs text-slate-400">
              Nenhum cliente encontrado para “{termo}”.
            </p>
          ) : (
            resultados.map((cliente) => (
              <button
                key={cliente.id}
                type="button"
                onClick={() => {
                  onChange(cliente);
                  setAberto(false);
                }}
                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left transition last:border-0 hover:bg-slate-50"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-[10px] font-bold text-pink-700">
                  {initialsOf(cliente.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-800">
                    {cliente.name}
                  </span>
                  <span className="block truncate text-[11px] text-slate-500">
                    #{cliente.id}
                    {cliente.email ? ` · ${cliente.email}` : ""}
                  </span>
                </span>
                <Badge tone={PLAN_TONE[cliente.planId] || "neutral"}>
                  {cliente.planId}
                </Badge>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ClientPicker;
